import {
  BackorderLineDTO,
  BackorderLinesResponse,
  BasketContactResponseDTO,
  BasketHasNoContactDTO,
  UserBasketDTO,
  BasketNameTooLongDTO, GetBasketOverviewForUserResponse,
  UserParty,
  BasketSelectionDTO,
  UserInfoViewValue,
  ConfiguredDeliveryPolicyDTO,
  CreateAdditionalBasketFailureDTO,
  CreateAdditionalBasketResponseDTO,
  DeliveryPolicyResponseDTO,
  GetDeliveryPoliciesResponseDTO,
  GetSelectedBasketResponseDTO,
  NameBasketFailureDTO,
  NameBasketResponseDTO,
  NoBasketSelectedDTO,
  NoNameGivenDTO,
  PossibleTransferBasketRecipientsDTO,
  TransferBasketRecipientDTO,
} from './api-types.js'
import {
  BackorderLine,
  BackOrderProductResponse,
  BasketContact,
  BasketHasNoContact,
  BasketItem,
  BasketNameTooLong,
  BasketOverview,
  BasketParty,
  BasketSelection,
  BasketUser,
  ConfiguredDeliveryPolicy,
  CreateAdditionalBasketFailure,
  CreateAdditionalBasketResponse,
  CreateAdditionalBasketUnionResponse,
  DeliveryPolicy,
  GetDeliveryPoliciesResponse,
  GetSelectedBasketResponse,
  NameBasketFailure,
  NameBasketResponse,
  NameBasketUnionResponse,
  NoBasketSelected,
  NoNameGiven,
  PossibleTransferBasketRecipients,
  TransferBasketRecipient,
  TrivialDeliveryPolicy,
} from './internal-types.js'


const assertType = (input: Record<string, any>, typeName: string): void => {
  if (input['@type'] !== typeName) {
    const message = 'expected type ' + typeName + ' but got ' + input['@type']
    throw Error(message)
  }
}

const isType = <T = any>(input: unknown, typeName: string): input is T => {
  return (input as { '@type': string })['@type'] === typeName
}

export const parseUserBasketDTO = (input: UserBasketDTO): BasketItem => {
  assertType(input, 'UserBasketDTO')
  return new BasketItem(
    input['basket_id'],
    parseBasketName(input['name']),
    parseDeliveryPolicyResponse(input['delivery_policy']),
    parseBasketUser(input['user']),
    parseBasketParty(input['party']),
  )
}

export const parseBasketUser = (input: UserInfoViewValue) => {
  assertType(input, 'UserInfoViewValue')
  return new BasketUser(
    input['first_name'],
    input['middle_name'],
    input['last_name'],
    input['email'],
  )
}

export const parseBasketParty = (input: UserParty) => {
  assertType(input, 'UserParty')
  return new BasketParty(
    input['party_id'],
    input['name'],
  )
}

export const parseBasketName = (input: string | NoNameGivenDTO): string | NoNameGiven => {
  return typeof input === 'string' ? input : parseNoNameGivenDTO(input)
}

export const parseBasketNameTooLongDTO = (input: BasketNameTooLongDTO): BasketNameTooLong => {
  assertType(input, 'BasketNameTooLongDTO')
  return new BasketNameTooLong(input['max_length'], input['supplied_length'])
}

export const parseGetBasketOverviewForUserResponse = (input: GetBasketOverviewForUserResponse): BasketOverview => {
  assertType(input, 'GetBasketOverviewForUserResponse')
  return new BasketOverview(input['baskets'].map((x: any) => parseUserBasketDTO(x)))
}

export const parseBasketSelection = (input: BasketSelectionDTO): BasketSelection => {
  return typeof input === 'string' ? input : parseNoBasketSelectedDTO(input)
}

export const parseConfiguredDeliveryPolicyDTO = (input: ConfiguredDeliveryPolicyDTO): ConfiguredDeliveryPolicy => {
  assertType(input, 'ConfiguredDeliveryPolicyDTO')
  return new ConfiguredDeliveryPolicy(
    input['id'],
    input['name'],
  )
}

export const parseCreateAdditionalBasketFailure = (input: CreateAdditionalBasketFailureDTO): CreateAdditionalBasketFailure => {
  assertType(input, 'CreateAdditionalBasketFailure')
  return new CreateAdditionalBasketFailure(
    parseBasketNameTooLongDTO(input['reason']),
  )
}

export const parseCreateAdditionalBasketResponse = (input: CreateAdditionalBasketResponseDTO): CreateAdditionalBasketResponse => {
  assertType(input, 'CreateAdditionalBasketResponse')
  return new CreateAdditionalBasketResponse(
    input['basket_id'],
  )
}

export const parseCreateAdditionalBasketUnionResponse = (input: any): CreateAdditionalBasketUnionResponse => {
  try {
    return parseCreateAdditionalBasketResponse(input)
  } catch {
    return parseCreateAdditionalBasketFailure(input)
  }
}

export const parseNameBasketUnionResponse = (input: any): NameBasketUnionResponse => {
  try {
    return parseNameBasketResponse(input)
  } catch {
    return parseNameBasketFailure(input)
  }
}

export const parseGetDeliveryPoliciesResponse = (input: GetDeliveryPoliciesResponseDTO): GetDeliveryPoliciesResponse => {
  assertType(input, 'GetDeliveryPoliciesResponse')
  return new GetDeliveryPoliciesResponse(
    input['delivery_policies'].map((x: DeliveryPolicyResponseDTO) => parseDeliveryPolicyResponse(x)),
    parseDeliveryPolicyResponse(input['default']),
  )
}

export const parseDeliveryPolicyResponse = (input: any): DeliveryPolicy => {
  try {
    return parseConfiguredDeliveryPolicyDTO(input)
  } catch {
    return parseTrivialDeliveryPolicyDTO(input)
  }
}

export const parseGetSelectedBasketResponse = (input: GetSelectedBasketResponseDTO): GetSelectedBasketResponse => {
  assertType(input, 'GetSelectedBasketResponse')
  return new GetSelectedBasketResponse(
    parseBasketSelection(input['basket_selection']),
  )
}

export const parseNameBasketFailure = (input: NameBasketFailureDTO): NameBasketFailure => {
  assertType(input, 'NameBasketFailure')
  return new NameBasketFailure(
    parseBasketNameTooLongDTO(input['reason']),
  )
}

export const parseNameBasketResponse = (input: NameBasketResponseDTO): NameBasketResponse => {
  assertType(input, 'NameBasketResponse')
  return new NameBasketResponse()
}

export const parseNoNameGivenDTO = (input: NoNameGivenDTO): NoNameGiven => {
  assertType(input, 'NoNameGivenDTO')
  return new NoNameGiven()
}

export const parseNoBasketSelectedDTO = (input: NoBasketSelectedDTO): NoBasketSelected => {
  assertType(input, 'NoBasketSelectedDTO')
  return new NoBasketSelected()
}

export const parsePossibleTransferBasketRecipients = (input: PossibleTransferBasketRecipientsDTO): PossibleTransferBasketRecipients => {
  assertType(input, 'PossibleTransferBasketRecipients')
  return new PossibleTransferBasketRecipients(input['recipients'].map(
    (x: TransferBasketRecipientDTO) => parseTransferBasketRecipient(x)),
  )
}

export const parseTransferBasketRecipient = (input: TransferBasketRecipientDTO): TransferBasketRecipient => {
  assertType(input, 'TransferBasketRecipient')
  return new TransferBasketRecipient(
    input['user_id'],
    input['name'],
    input['email_address'],
  )
}

export const parseTrivialDeliveryPolicyDTO = (input: Record<string, any>): TrivialDeliveryPolicy => {
  assertType(input, 'TrivialDeliveryPolicyDTO')
  if (input['@type'] !== 'TrivialDeliveryPolicyDTO') {
    throw Error()
  }
  return new TrivialDeliveryPolicy()
}

export const parseBackOrderLine = (input: BackorderLineDTO): BackorderLine => {
  assertType(input, 'BackorderLine')
  return new BackorderLine(
    1234,
    input['expected_delivery_date'],
    input['product_id'],
    input['quantity'],
    input['sale_date'],
    input['sale_reference'],
    input['customer_order_number'],
    input['erp_order_number'],
  )
}

export const parseBackorderProductResponse = (input: BackorderLinesResponse): BackOrderProductResponse => {
  assertType(input, 'BackorderLinesResponse')
  return new BackOrderProductResponse(input['lines'].map(
    (x: BackorderLineDTO) => parseBackOrderLine(x),
  ), input['total_backorder_count'])
}

const parseBasketContactDTO = (input: BasketContactResponseDTO): BasketContact => {
  assertType(input, 'BasketContactResponse')
  return { name: input.name, telephone: input.telephone }
}

const parseBasketHasNoContactDTO = (input: BasketHasNoContactDTO): BasketHasNoContact => {
  assertType(input, 'BasketHasNoConact')
  return new BasketHasNoContact()
}

export const parseBasketContactResponse = (input: any): BasketContact | BasketHasNoContact => {
  if (isType<BasketContactResponseDTO>(input, 'BasketContactResponse')) return parseBasketContactDTO(input)
  if (isType<BasketHasNoContactDTO>(input, 'BasketHasNoConact')) return parseBasketHasNoContactDTO(input)
  throw new Error('Error parsing BasketContactResponse')
}
